// Pure helpers for manually editing assigned routes. Assigned routes are GeoJSON
// LineString features: { properties: { trips, destination, direction }, geometry:
// { coordinates: [[lng,lat], …] } }. These functions never mutate their inputs —
// they return new coordinate arrays / totals — so they're easy to unit-test and
// safe to use with React state.

// Recompute the assignment headline totals from the (possibly edited) features.
// arr/dep split by the route's `direction`.
export function recomputeAssignedTotals(features = []) {
  let total = 0, arr = 0, dep = 0;
  for (const f of features) {
    const t = Math.round((f.properties && f.properties.trips) || 0);
    total += t;
    if (f.properties && f.properties.direction === 'arrivals') arr += t;
    else dep += t;
  }
  return { total_assigned: total, arr_assigned: arr, dep_assigned: dep };
}

// Move the vertex at `index` to `[lng, lat]`. Out-of-range index → unchanged copy.
export function moveVertex(coords, index, lng, lat) {
  if (index < 0 || index >= coords.length) return coords.map(c => [...c]);
  return coords.map((c, i) => (i === index ? [lng, lat] : [...c]));
}

// Insert a new vertex `[lng, lat]` at position `index` (0..length). Clamped.
export function insertVertex(coords, index, lng, lat) {
  const i = Math.max(0, Math.min(index, coords.length));
  const out = coords.map(c => [...c]);
  out.splice(i, 0, [lng, lat]);
  return out;
}

// Remove the vertex at `index`, but never drop below 2 points (a line needs two).
export function removeVertex(coords, index) {
  if (coords.length <= 2 || index < 0 || index >= coords.length) return coords.map(c => [...c]);
  return coords.filter((_, i) => i !== index);
}

// Midpoints of each segment, for the "click to insert a vertex here" handles.
// Returns [{ lng, lat, insertAt }] where insertAt is the coords index to splice at.
export function midpoints(coords = []) {
  const out = [];
  for (let i = 0; i < coords.length - 1; i++) {
    const [alng, alat] = coords[i];
    const [blng, blat] = coords[i + 1];
    out.push({ lng: (alng + blng) / 2, lat: (alat + blat) / 2, insertAt: i + 1 });
  }
  return out;
}

// Find the feature matching a {destination, direction} selector.
export function findRouteIndex(features = [], destination, direction) {
  return features.findIndex(f => f.properties
    && f.properties.destination === destination
    && f.properties.direction === direction);
}
